import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useUserStore } from "../../store";
import { getCsrfToken } from "../../utils";

import { getRoleLabel } from '../../utils/roles';
function InvitationAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, fetchUser } = useUserStore();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);

  // Signup form state (for new users)
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordRules, setPasswordRules] = useState({
    minLength: false,
    hasNumberOrSpecial: false,
  });

  // Password validation helper
  const validatePasswordRules = (pwd) => ({
    minLength: pwd.length >= 8,
    hasNumberOrSpecial: /[0-9]/.test(pwd) || /[^a-zA-Z0-9]/.test(pwd),
  });

  useEffect(() => {
    fetchInvitationDetails();
  }, [token]);

  const fetchInvitationDetails = async () => {
    try {
      const response = await axios.get(`/api/invitations/${token}/`);
      setInvitation(response.data);
      // Prefill name with linked employee name if available
      if (response.data.employee_name) {
        setName(response.data.employee_name);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t("Failed to load invitation"));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptExistingUser = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(
        `/api/invitations/${token}/accept/`,
        {},
        {
          headers: { "X-CSRFToken": getCsrfToken() },
        }
      );
      await fetchUser(); // Refresh user data
      navigate("/"); // Go to dashboard
    } catch (err) {
      setError(err.response?.data?.detail || t("Failed to accept invitation"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError(null);

    const rules = validatePasswordRules(password);
    if (!rules.minLength) {
      setPasswordError(t("Password must be at least 8 characters"));
      return;
    }
    if (!rules.hasNumberOrSpecial) {
      setPasswordError(t("Password must contain at least one number or special character"));
      return;
    }

    if (!name.trim()) {
      setError(t("Name is required"));
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`/api/invitations/${token}/signup/`, {
        name,
        password,
      });
      // User is auto-logged in, redirect to dashboard
      window.location.href = "/";
    } catch (err) {
      setError(err.response?.data?.detail || t("Failed to create account"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "grey.100",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error && !invitation) {
    return (
      <Box
        sx={{
          maxWidth: 500,
          mx: "auto",
          mt: 8,
          p: 2,
        }}
      >
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Button variant="outlined" onClick={() => navigate("/accounts/login/")}>
            {t("Go to Login")}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
      }}
    >
      <Card sx={{ maxWidth: 500, width: "100%" }}>
        <CardContent sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            {t("You're Invited!")}
          </Typography>

          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {invitation?.invited_by_name
              ? t("{{name}} has invited you to join {{org}}", {
                  name: invitation.invited_by_name,
                  org: invitation.organization_name,
                })
              : t("You've been invited to join {{org}}", {
                  org: invitation?.organization_name,
                })}
          </Typography>

          <Box
            sx={{
              bgcolor: "grey.50",
              p: 2,
              borderRadius: 1,
              mb: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              <strong>{t("Organization")}:</strong> {invitation?.organization_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              <strong>{t("Role")}:</strong>{" "}
              {getRoleLabel(invitation?.role || "employee", t)}
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {invitation?.user_exists || user ? (
            // Existing user flow
            <>
              {user ? (
                // User is logged in
                user.email?.toLowerCase() === invitation?.email?.toLowerCase() ? (
                  <Box>
                    <Typography sx={{ mb: 2 }}>
                      {t("Click below to accept the invitation and join the organization.")}
                    </Typography>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAcceptExistingUser}
                      disabled={submitting}
                      size="large"
                    >
                      {submitting ? <CircularProgress size={24} /> : <span>{t("Accept Invitation")}</span>}
                    </Button>
                  </Box>
                ) : (
                  <Alert severity="warning">
                    {t("This invitation was sent to {{email}}. Please log in with that account.", {
                      email: invitation?.email,
                    })}
                    <Box sx={{ mt: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => navigate("/accounts/logout/")}
                      >
                        {t("Log out and switch account")}
                      </Button>
                    </Box>
                  </Alert>
                )
              ) : (
                // User exists but not logged in
                <>
                  <Typography sx={{ mb: 2 }}>
                    {t("An account exists with this email. Please log in to accept the invitation.")}
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={() => navigate(`/accounts/login/?next=/invitation/${token}`)}
                  >
                    {t("Log in to Accept")}
                  </Button>
                </>
              )}
            </>
          ) : (
            // New user signup flow
            <form onSubmit={handleSignup}>
              <Typography sx={{ mb: 2 }}>
                {t("Create your account to join the team.")}
              </Typography>

              <TextField
                fullWidth
                label={t("Email")}
                value={invitation?.email || ""}
                disabled
                sx={{ mb: 2 }}
                size="small"
              />

              <TextField
                fullWidth
                label={t("Your Name")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                sx={{ mb: 2 }}
                size="small"
                autoFocus
              />

              <TextField
                fullWidth
                type="password"
                label={t("Password")}
                value={password}
                onChange={(e) => {
                  const newPassword = e.target.value;
                  setPassword(newPassword);
                  setPasswordError("");
                  setPasswordRules(validatePasswordRules(newPassword));
                }}
                error={!!passwordError}
                helperText={passwordError}
                required
                sx={{ mb: 1 }}
                size="small"
              />

              {/* Password requirements checklist */}
              <Box sx={{ mb: 3, ml: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: password.length === 0
                      ? "text.secondary"
                      : passwordRules.minLength
                        ? "success.main"
                        : "error.main",
                  }}
                >
                  {passwordRules.minLength ? "\u2713" : "\u25CB"} {t("At least 8 characters")}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    color: password.length === 0
                      ? "text.secondary"
                      : passwordRules.hasNumberOrSpecial
                        ? "success.main"
                        : "error.main",
                  }}
                >
                  {passwordRules.hasNumberOrSpecial ? "\u2713" : "\u25CB"} {t("Contains a number or special character")}
                </Typography>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={submitting}
                size="large"
              >
                {submitting ? <CircularProgress size={24} /> : <span>{t("Create Account & Join")}</span>}
              </Button>
            </form>
          )}

          <Box sx={{ mt: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("Having trouble?")}{" "}
              <a href="mailto:asiakaspalvelu@verstaani.fi">{t("Contact support")}</a>
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default InvitationAcceptPage;
